import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * What a staff screen hands to its notice beside it (docs/design/panel-v20/staff-v20.md §1.6): the TPV's reserved
 * place, a row of its own under the table list, where the notice is drawn instead of floating over the tables. Every
 * notice itself, Servicio's coverage included, comes from the recipient's inbox.
 * The screen and the notice are siblings; this context is the only thing they share.
 */
interface StaffScreenNotices {
  readonly dock: HTMLElement | null;
  readonly setDock: (node: HTMLElement | null) => void;
}

const StaffScreenNoticesContext = createContext<StaffScreenNotices | null>(null);

export function StaffScreenNoticesProvider({ children }: { readonly children: ReactNode }) {
  const [dock, setDock] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ dock, setDock }), [dock]);
  return <StaffScreenNoticesContext.Provider value={value}>{children}</StaffScreenNoticesContext.Provider>;
}

/** The notice side: the TPV's reserved place (null elsewhere). */
export function useStaffScreenNotices(): Pick<StaffScreenNotices, 'dock'> {
  const context = useContext(StaffScreenNoticesContext);
  return { dock: context?.dock ?? null };
}

/** The TPV's reserved place: a ref for the row under its table list. Outside a staff screen it does nothing. */
export function useStaffNoticeDock(): (node: HTMLElement | null) => void {
  const setDock = useContext(StaffScreenNoticesContext)?.setDock;
  return useCallback((node: HTMLElement | null) => setDock?.(node), [setDock]);
}
